import dynamic from 'next/dynamic';

// === Always synchronous imports ===
// Core layout + hero blocks that render above the fold on almost every page.
// Keeping them static avoids chunk-load delay on initial render.
import NavigationBlock from './layout-blocks/NavigationBlock';
import HeroBlock from './layout-blocks/HeroBlock';
import HeroLandingBlock from './layout-blocks/HeroLandingBlock';
import HeroCampaignBlock from './layout-blocks/HeroCampaignBlock';
import LightBlock from './layout-blocks/LightBlock';
import DarkBlock from './layout-blocks/DarkBlock';
import FooterBlock from './layout-blocks/FooterBlock';

// Navigation menu items (inline in NavigationBlock)
import MenuItemBlockLinks from './navigation/MenuItemBlockLinks';
import MenuItemBlockHelp from './navigation/MenuItemBlockHelp';
import MenuItemBlockDirectLink from './navigation/MenuItemBlockDirectLink';

// Frequent text/layout primitives used by many pages
import H1 from './components/H1';
import H2 from './components/H2';
import H4 from './components/H4';
import H5 from './components/H5';
import TextBlock from './components/TextBlock';
import Buttons from './components/Buttons';
import CtaContainer from './components/CtaContainer';
import Block from './components/Block';
import Columns from './components/Columns';
import HorizontalSpace from './components/HorizontalSpace';
import Line from './components/Line';
import ImageBlock from './components/ImageBlock';
import RichTextBlock from './components/RichTextBlock';

// === Dynamic (code-split) imports ===
// Below-the-fold, rare, or heavy blocks. Next.js still SSRs them (ssr: true by
// default), so SEO and initial HTML are unaffected. Client only downloads the
// chunks for blocks actually used on the current page.
const BannerBlock = dynamic(() => import('./components/BannerBlock'));
const SquaresBlock = dynamic(() => import('./components/SquaresBlock'));
const ImageBoxBlock = dynamic(() => import('./components/ImageBoxBlock'));
const DynamicBlock = dynamic(() => import('./components/DynamicBlock'));
const IconsList = dynamic(() => import('./components/IconsList'));
const FeaturesCardsContainer = dynamic(() => import('./components/FeaturesCardsContainer'));
const FeaturesSmallCardsContainer = dynamic(() => import('./components/FeaturesSmallCardsContainer'));
const FeaturesSimpleCardsContainer = dynamic(() => import('./components/FeaturesSimpleCardsContainer'));
const FeaturesNumberedCardsContainer = dynamic(() => import('./components/FeaturesNumberedCardsContainer'));
const FeaturesHighlightGallery = dynamic(() => import('./components/FeaturesHighlightGallery'));
const TestimonialsBlock = dynamic(() => import('./components/TestimonialsBlock'));
const TestimonialsCarousel = dynamic(() => import('./components/TestimonialsCarousel'));
const Testimonial = dynamic(() => import('./components/Testimonial'));
const ReferencesBlock = dynamic(() => import('./components/ReferencesBlock'));
const ReferencesCarousel = dynamic(() => import('./components/ReferencesCarousel'));
const ImagesCarousel = dynamic(() => import('./components/ImagesCarousel'));
const HowItWorksCarousel = dynamic(() => import('./components/HowItWorksCarousel'));
const TimelineCarousel = dynamic(() => import('./components/TimelineCarousel'));
const SmsCaseStudyCarousel = dynamic(() => import('./components/SmsCaseStudyCarousel'));
const SmsHeroBlock = dynamic(() => import('./components/SmsHeroBlock'));
const SmsPricingCardsBlock = dynamic(() => import('./components/SmsPricingCardsBlock'));
const FullImageCtaBlock = dynamic(() => import('./components/FullImageCtaBlock'));
const ColumnsImageWithTextBlock = dynamic(() => import('./components/ColumnsImageWithTextBlock'));
const CardsWithImageBlock = dynamic(() => import('./components/CardsWithImageBlock'));
const CaseStudiesBlock = dynamic(() => import('./components/CaseStudiesBlock'));
const QuestionsBlock = dynamic(() => import('./components/QuestionsBlock'));
const ClaimWithNumber = dynamic(() => import('./components/ClaimWithNumber'));
const ClientLineBlocks = dynamic(() => import('./components/ClientLineBlocks'));
const PlatformsStrip = dynamic(() => import('./components/PlatformsStrip'));
const PricingBlock = dynamic(() => import('./components/PricingBlock'));
const SimpleTable = dynamic(() => import('./components/SimpleTable'));
const StatsTable = dynamic(() => import('./components/StatsTable'));
const SharedComponent = dynamic(() => import('./components/SharedComponent'));
const RichTextMarkdownBlock = dynamic(() => import('./components/RichTextMarkdownBlock'));
const HtmlBlock = dynamic(() => import('./components/HtmlBlock'));
const CookiesBlock = dynamic(() => import('./components/CookiesBlock'));
const TutorialsBlock = dynamic(() => import('./components/TutorialsBlock'));
const WebinarsBlock = dynamic(() => import('./components/WebinarsBlock'));
const UpcomingWebinarsBlock = dynamic(() => import('./components/UpcomingWebinarsBlock'));
const MaterialsBlock = dynamic(() => import('./components/MaterialsBlock'));
const SegmentationBlock = dynamic(() => import('./components/SegmentationBlock'));
const QuizBlock = dynamic(() => import('./components/QuizBlock'));
const GlossaryListBlock = dynamic(() => import('./components/GlossaryListBlock'));
const ServicesCardsBlock = dynamic(() => import('./components/services/ServicesCardsBlock'));
const ServicesAccordionBlock = dynamic(() => import('./components/services/ServicesAccordionBlock'));
const ServicesInquiryFormBlock = dynamic(() => import('./components/services/ServicesInquiryFormBlock'));

// Glossary detail components (only rendered on glossary term pages)
const GlossaryRichText = dynamic(() => import('./components/glossary/GlossaryRichText'));
const GlossaryTable = dynamic(() => import('./components/glossary/GlossaryTable'));
const GlossaryCallout = dynamic(() => import('./components/glossary/GlossaryCallout'));
const GlossarySegmentsShowcase = dynamic(() => import('./components/glossary/GlossarySegmentsShowcase'));
const GlossaryInlineCta = dynamic(() => import('./components/glossary/GlossaryInlineCta'));
const GlossaryFaq = dynamic(() => import('./components/glossary/GlossaryFaq'));
const GlossaryCtaDark = dynamic(() => import('./components/glossary/GlossaryCtaDark'));
const GlossaryFurtherReading = dynamic(() => import('./components/glossary/GlossaryFurtherReading'));

export const LAYOUT_BLOCKS = 'layout-blocks';
export const COMPONENTS = 'components';
export const NAVIGATION = 'navigation';
export const GLOSSARY = 'glossary';

const components = {
  [LAYOUT_BLOCKS]: {
    ComponentLayoutBlocksNavigationBlock: NavigationBlock,
    ComponentLayoutBlocksHeroBlock: HeroBlock,
    ComponentLayoutBlocksHeroLandingBlock: HeroLandingBlock,
    ComponentLayoutBlocksLightBlock: LightBlock,
    ComponentLayoutBlocksDarkBlock: DarkBlock,
    ComponentLayoutBlocksFooterBlock: FooterBlock,
    ComponentLayoutBlocksHeroCampaignBlock: HeroCampaignBlock
  },
  [COMPONENTS]: {
    ComponentComponentsFeaturesCardsContainer: FeaturesCardsContainer,
    ComponentComponentsFeaturesSmallCardsContainer: FeaturesSmallCardsContainer,
    ComponentComponentsBannerBlock: BannerBlock,
    ComponentComponentsCtaContainer: CtaContainer,
    ComponentComponentsColumns: Columns,
    ComponentComponentsTextBlock: TextBlock,
    ComponentComponentsSquaresBlock: SquaresBlock,
    ComponentComponentsImageBlock: ImageBlock,
    ComponentComponentsImageBoxBlock: ImageBoxBlock,
    ComponentComponentsH1: H1,
    ComponentComponentsH2: H2,
    ComponentComponentsH4: H4,
    ComponentComponentsH5: H5,
    ComponentComponentsDynamicBlock: DynamicBlock,
    ComponentComponentsIconsList: IconsList,
    ComponentComponentsHorizontalSpace: HorizontalSpace,
    ComponentComponentsTestimonialsBlock: TestimonialsBlock,
    ComponentComponentsTestimonialsCarousel: TestimonialsCarousel,
    ComponentComponentsFullImageCtaBlock: FullImageCtaBlock,
    ComponentComponentsSmsHeroBlock: SmsHeroBlock,
    ComponentComponentsLine: Line,
    ComponentComponentsReferencesBlock: ReferencesBlock,
    ComponentComponentsReferencesCarousel: ReferencesCarousel,
    ComponentComponentsClaimWithNumber: ClaimWithNumber,
    ComponentComponentsColumnsImageWithTextBlock: ColumnsImageWithTextBlock,
    ComponentComponentsQuestionsBlock: QuestionsBlock,
    ComponentComponentsBlock: Block,
    ComponentComponentsFeaturesSimpleCardsContainer: FeaturesSimpleCardsContainer,
    ComponentComponentsFeaturesNumberedCardsContainer: FeaturesNumberedCardsContainer,
    ComponentComponentsButtons: Buttons,
    ComponentComponentsSmsCaseStudyCarousel: SmsCaseStudyCarousel,
    ComponentComponentsRichText: RichTextBlock,
    ComponentComponentsRichTextMarkdown: RichTextMarkdownBlock,
    ComponentComponentsHtmlBlock: HtmlBlock,
    ComponentComponentsSmsPricingCardsBlock: SmsPricingCardsBlock,
    ComponentComponentsImagesCarousel: ImagesCarousel,
    ComponentComponentsFeaturesHighlightGallery: FeaturesHighlightGallery,
    ComponentComponentsCardsWithImageBlock: CardsWithImageBlock,
    ComponentComponentsSimpleTable: SimpleTable,
    ComponentComponentsPricingBlock: PricingBlock,
    ComponentComponentsSharedComponent: SharedComponent,
    ComponentComponentsStatsTable: StatsTable,
    ComponentComponentsCaseStudiesBlock: CaseStudiesBlock,
    ComponentComponentsCookiesBlock: CookiesBlock,
    ComponentComponentsTestimonials: Testimonial,
    ComponentComponentsClientLineBlocks: ClientLineBlocks,
    ComponentComponentsPlatformsStrip: PlatformsStrip,
    ComponentComponentsTutorialsBlock: TutorialsBlock,
    ComponentComponentsWebinarsBlock: WebinarsBlock,
    ComponentComponentsUpcomingWebinarsBlock: UpcomingWebinarsBlock,
    ComponentComponentsMaterialsBlock: MaterialsBlock,
    ComponentComponentsHowItWorksCarousel: HowItWorksCarousel,
    ComponentComponentsTimelineCarousel: TimelineCarousel,
    ComponentComponentsSegmentationBlock: SegmentationBlock,
    ComponentComponentsQuizBlock: QuizBlock,
    ComponentComponentsGlossaryListBlock: GlossaryListBlock,
    ComponentComponentsServicesCardsBlock: ServicesCardsBlock,
    ComponentComponentsServicesAccordionBlock: ServicesAccordionBlock,
    ComponentComponentsServicesInquiryFormBlock: ServicesInquiryFormBlock
  },
  [NAVIGATION]: {
    ComponentNavigationMenuItemBlockLinks: MenuItemBlockLinks,
    ComponentNavigationMenuItemBlockHelp: MenuItemBlockHelp,
    ComponentNavigationMenuItemBlockDirectLink: MenuItemBlockDirectLink
  },
  [GLOSSARY]: {
    ComponentComponentsRichTextMarkdown: GlossaryRichText,
    ComponentGlossaryTable: GlossaryTable,
    ComponentGlossaryCallout: GlossaryCallout,
    ComponentGlossarySegmentsShowcase: GlossarySegmentsShowcase,
    ComponentGlossaryInlineCta: GlossaryInlineCta,
    ComponentGlossaryFaq: GlossaryFaq,
    ComponentGlossaryCtaDark: GlossaryCtaDark,
    ComponentGlossaryFurtherReading: GlossaryFurtherReading
  }
};

export default components;
